import React, { useEffect, useState, useMemo } from 'react';
import { Form, FormFeedback, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { saveAs } from 'file-saver';
import { createOption, Option } from '../../utils';
import { Campaign } from '../../types/campaign';
import Select, { ActionMeta, SingleValue } from 'react-select';
import { getDownloadBlockedReport, getDownloadGeneralReport, getDownloadVerificationReport } from '../../api/reportApi';
import Flatpickr from 'react-flatpickr';
import { toast } from 'react-toastify';

interface DownloadReportModalProps {
    campaign: Campaign;
    isOpen: boolean;
    toggle: () => void;
}

const DownloadReportModal = ({ campaign, isOpen, toggle }: DownloadReportModalProps) => {
    const [loading, setLoading] = useState<boolean>(false);
    const [selectReportTypeValue, setSelectReportTypeValue] = useState<SingleValue<Option>>(createOption('Geral', 'general'));
    const [selectReportFormatValue, setSelectReportFormatValue] = useState<SingleValue<Option>>(createOption('xlsx', 'xlsx'));
    const selectReportTypeOptions = [
        createOption('Geral', 'general'),
        createOption('Verificação', 'verification'),
        createOption('Bloqueados', 'blocked'),
    ];
    const selectReportFormatOptions = [
        createOption('xlsx', 'xlsx'),
        createOption('csv', 'csv'),
        createOption('json', 'json'),
    ];

    const toggleModal = () => {
        toggle();
    };

    const selectReportTypeOnChange = (newValue: SingleValue<Option>, actionMeta?: ActionMeta<Option>) => {
        setSelectReportTypeValue(newValue);
        handleForm.setFieldValue('type', newValue ? newValue.value : '');
    };

    const selectReportFormatOnChange = (newValue: SingleValue<Option>, actionMeta?: ActionMeta<Option>) => {
        // This function is not used in the current implementation, but can be used if you want to handle format selection
        setSelectReportFormatValue(newValue);
        handleForm.setFieldValue('format', newValue ? newValue.value : '');
    };

    // Always keep rangeValue as an array of two dates or []
    const [rangeValue, setRangeValue] = useState<Date[]>([]);

    // Memoize the default periodo string based on rangeValue
    const defaultPeriodo = useMemo(() => {
        if (rangeValue.length === 2) {
            return rangeValue.map(d => d.toISOString().split('T')[0]).join(' - ');
        }
        return '';
    }, [rangeValue]);

    function getFileNameFromContentDisposition(header: string): string | null {
        const match = header.match(/filename="?([^"]+)"?/);
        if (match && match[1]) {
            return match[1];
        }
        return null;
    }

    const initialValues = {
        type: selectReportTypeValue?.value || 'general',
        periodo: defaultPeriodo,
        format: selectReportFormatValue?.value || 'xlsx',
        maxXlsxSizeMb: 10,
        compress: true,
        removeXlsxHeader: false, // Added field
    };

    const handleForm: any = useFormik({
        enableReinitialize: true,
        initialValues: initialValues,
        validationSchema: Yup.object({
            type: Yup.string().required('O tipo do relatório é obrigatório'),
            format: Yup.string(),
            // Optionally add validation for removeXlsxHeader if needed
        }),
        onSubmit: (values: any) => {
            if (!values.periodo) {
                toast.error('Período é obrigatório');
                return;
            }
            setLoading(true);
            const [startDate, endDate] = values.periodo.split(' - ');
            values.startDate = startDate;
            values.endDate = endDate;
            const type = values.type || selectReportTypeValue?.value || 'general';
            delete values.periodo;
            delete values.type;
            if (type === 'general') {
                getDownloadGeneralReport(campaign.id, values)
                    .then((res) => {
                        const fileName = getFileNameFromContentDisposition(res.headers['content-disposition']);
                        if (fileName) {
                            saveAs(res.data, fileName);
                            toast.success('Relatório baixado com sucesso!');
                            toggleModal();
                        } else {
                            toast.error('Erro ao obter o nome do arquivo. Tente novamente.');
                        }
                    })
                    .catch((error: any) => {
                        toast.error('Erro ao baixar o relatório. Tente novamente.');
                        console.error('Download error:', error);
                    })
                    .finally(() => {
                        setLoading(false);
                    });
            } else if (type === 'verification') {
                getDownloadVerificationReport(campaign.id, values)
                    .then((res) => {
                        const fileName = getFileNameFromContentDisposition(res.headers['content-disposition']);
                        if (fileName) {
                            saveAs(res.data, fileName);
                            toast.success('Relatório baixado com sucesso!');
                            toggleModal();
                        } else {
                            toast.error('Erro ao obter o nome do arquivo. Tente novamente.');
                        }
                    })
                    .catch((error: any) => {
                        toast.error('Erro ao baixar o relatório. Tente novamente.');
                        console.error('Download error:', error);
                    })
                    .finally(() => {
                        setLoading(false);
                    });
            } else if (type === 'blocked') {
                getDownloadBlockedReport(campaign.id, values)
                    .then((res) => {
                        const fileName = getFileNameFromContentDisposition(res.headers['content-disposition']);
                        if (fileName) {
                            saveAs(res.data, fileName);
                            toast.success('Relatório baixado com sucesso!');
                            toggleModal();
                        } else {
                            toast.error('Erro ao obter o nome do arquivo. Tente novamente.');
                        }
                    })
                    .catch((error: any) => {
                        toast.error('Erro ao baixar o relatório. Tente novamente.');
                        console.error('Download error:', error);
                    })
                    .finally(() => {
                        setLoading(false);
                    });
            }
        },
    });

    const handleRangeDatesChange = (newValue: Date[]) => {
        setRangeValue(newValue);
        handleForm.setFieldValue('periodo', newValue.map(d => d.toISOString().split('T')[0]).join(' - '));
    };

    useEffect(() => {
        // Find the earliest start date and latest end date from the campaign orders
        const startDate = campaign.orders.reduce(
            (min, o) => new Date(o.startDate) < new Date(min) ? o.startDate : min,
            campaign.orders[0].startDate
        );
        const endDate = campaign.orders.reduce(
            (max, o) => new Date(o.endDate) > new Date(max) ? o.endDate : max,
            campaign.orders[0].endDate
        );
        // Convert to local dates (avoid timezone issues)
        const localStartDate = new Date(new Date(startDate).setHours(0, 0, 0, 0));
        const localEndDate = new Date(new Date(endDate).setHours(23, 59, 59, 59));
        const dateRange = [localStartDate, localEndDate];
        setRangeValue(dateRange);
    }, [campaign.orders]);

    return (
        <Modal id="showModal" isOpen={isOpen} toggle={toggleModal} fade={false} keyboard={true}
               backdrop={false} centered>
            <Form onSubmit={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleForm.handleSubmit();
                return false;
            }}>
                <ModalHeader toggle={toggleModal} className="bg-light p-3">
                    Baixar relatório
                </ModalHeader>

                <ModalBody>
                    <div className="mb-3">
                        <Label
                            htmlFor="type"
                            className="form-label"
                        >
                            Tipo de relatório
                        </Label>
                        <Select
                            isClearable={true}
                            isSearchable={true}
                            name="type"
                            options={selectReportTypeOptions}
                            styles={{
                                control: (baseStyles, state) => ({
                                    ...baseStyles,
                                    borderColor: !!(handleForm.touched.type && handleForm.errors.type) ? 'red' : '#ced4da',
                                }),
                            }}
                            onChange={selectReportTypeOnChange}
                            value={selectReportTypeValue}
                        />
                        {handleForm.touched.type && handleForm.errors.type ? (
                            <FormFeedback
                                type="invalid"
                                style={{display: 'block'}}>{handleForm.errors.type}</FormFeedback>
                        ) : null}
                    </div>

                    <div className="mb-3">
                        <Label
                            htmlFor="periodo"
                            className="form-label"
                        >
                            Período
                        </Label>
                        <Flatpickr
                            className="form-control"
                            id="datepicker-publish-input"
                            placeholder="Selecione um período"
                            value={rangeValue}
                            onChange={handleRangeDatesChange}
                            options={{
                                altInput: true,
                                altFormat: "d/m/Y",
                                mode: "range",
                                dateFormat: "d/m/Y",
                                minDate: new Date(new Date().setFullYear(new Date().getFullYear() - 2)),
                                maxDate: new Date(),
                            }}
                        />
                        {handleForm.touched.periodo && handleForm.errors.periodo ? (
                            <FormFeedback
                                type="invalid"
                                style={{display: 'block'}}>{handleForm.errors.periodo}</FormFeedback>
                        ) : null}
                    </div>

                    <div className="mb-3">
                        <Label
                            htmlFor="format"
                            className="form-label"
                        >
                            Formato
                        </Label>
                        <Select
                            isClearable={true}
                            isSearchable={true}
                            name="format"
                            options={selectReportFormatOptions}
                            styles={{
                                control: (baseStyles, state) => ({
                                    ...baseStyles,
                                    borderColor: !!(handleForm.touched.format && handleForm.errors.format) ? 'red' : '#ced4da',
                                }),
                            }}
                            onChange={selectReportFormatOnChange}
                            value={selectReportFormatValue}
                        />
                        {handleForm.touched.format && handleForm.errors.format ? (
                            <FormFeedback
                                type="invalid"
                                style={{display: 'block'}}>{handleForm.errors.format}</FormFeedback>
                        ) : null}
                    </div>

                    <div className="mb-3">
                        <Label
                            htmlFor="maxXlsxSizeMb"
                            className="form-label"
                        >
                            Tamanho máximo do output (MB)
                        </Label>
                        <Input
                            type="number"
                            name="maxXlsxSizeMb"
                            min={1}
                            value={handleForm.values.maxXlsxSizeMb}
                            onChange={handleForm.handleChange}
                        />
                    </div>

                    <div className="mb-3">
                        <div>
                            <label>
                                <input
                                    type="checkbox"
                                    name="compress"
                                    checked={handleForm.values.compress}
                                    onChange={handleForm.handleChange}
                                />
                                Compactar relatório
                            </label>
                        </div>
                    </div>

                    {handleForm.values.format === 'xlsx' && (
                        <div className="mb-3">
                            <div>
                                <label>
                                    <input
                                        type="checkbox"
                                        name="removeXlsxHeader"
                                        checked={handleForm.values.removeXlsxHeader}
                                        onChange={handleForm.handleChange}
                                    />
                                    Remover cabeçalho do arquivo XLSX
                                </label>
                            </div>
                        </div>
                    )}
                </ModalBody>
                <ModalFooter>
                    <button
                        type="submit"
                        className="btn btn-success btn-label right ms-auto nexttab nexttab"
                    >
                        <i className="ri-arrow-right-line label-icon align-middle fs-16 ms-2"></i>
                        {loading && <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>}
                        Baixar relatório
                    </button>
                </ModalFooter>
            </Form>
        </Modal>
    );
};

export default DownloadReportModal;
